import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { fetchConfig, debounce } from "@theme/utilities";

const CONFIG_ID = "sc-cart-goals-gift-config";
const FREE_PROP = "_free_product";
const ELEMENT_TAG = "sc-cart-goals-gift";
const SOURCE_ID = "sc-cart-goals-gift";

async function fetchCartJson() {
  return (await fetch("/cart.js")).json();
}

/** @typedef {{ variantId: number, thresholdCents: number }} ScGiftRule */
/** @typedef {{ propertyKey: string, gifts: ScGiftRule[], excludedProductIds?: number[] }} ScGiftConfig */

/**
 * Subtotal in cents excluding free-gift lines and products the merchant excluded in theme settings.
 * @param {Record<string, any>} cart
 * @param {string} propKey
 * @param {number[] | undefined} excludedProductIds
 */
function eligibleSubtotalCents(cart, propKey, excludedProductIds) {
  if (!cart.items?.length) return 0;
  const excluded = new Set(
    (Array.isArray(excludedProductIds) ? excludedProductIds : []).map((id) =>
      Number(id),
    ),
  );
  let sum = 0;
  for (const item of cart.items) {
    if (excluded.has(Number(item.product_id))) continue;
    if (
      item.properties?.[propKey] != null &&
      String(item.properties[propKey]).length > 0
    )
      continue;
    sum += item.final_line_price;
  }
  return sum;
}

/** @param {Record<string, any>} item @param {string} propKey */
function isFreeGiftLine(item, propKey) {
  return (
    item.properties?.[propKey] != null &&
    String(item.properties[propKey]).length > 0
  );
}

/**
 * Every cart line for this gift variant, with or without the free-gift property.
 * A second line without the property is still the same gift (Shopify keeps them apart).
 * @param {Record<string, any>} cart
 * @param {number} variantId
 */
function linesForVariant(cart, variantId) {
  const id = Number(variantId);
  return (cart.items || []).filter(
    /** @param {Record<string, any>} i */ (i) => Number(i.variant_id) === id,
  );
}

/**
 * @param {string} key
 * @param {number} quantity
 * @param {string} sectionsParam
 * @param {Record<string, string> | undefined} properties
 */
async function changeGiftLine(key, quantity, sectionsParam, properties) {
  /** @type {Record<string, any>} */
  const payload = {
    id: key,
    quantity,
    sections: sectionsParam,
    sections_url: window.location.pathname,
  };
  if (properties) payload.properties = properties;
  const res = await fetch(
    Theme.routes.cart_change_url,
    fetchConfig("json", { body: JSON.stringify(payload) }),
  );
  const data = JSON.parse(await res.text());
  if (data.errors || data.status) return null;
  dispatchSectionRefresh(data);
  const cart = await fetchCartJson();
  return cart?.items ? cart : null;
}

/**
 * Leaves exactly `quantity` (0 or 1) of the gift variant in the cart.
 * @param {Record<string, any>} cart
 * @param {number} variantId
 * @param {string} propKey
 * @param {0 | 1} quantity
 * @param {string} sectionsParam
 * @returns {Promise<Record<string, any> | null>}
 */
async function enforceGiftQuantity(cart, variantId, propKey, quantity, sectionsParam) {
  const lines = () => linesForVariant(cart, variantId);

  if (quantity === 0) {
    for (const line of lines()) {
      const next = await changeGiftLine(line.key, 0, sectionsParam);
      if (!next) return null;
      cart = next;
    }
    return cart;
  }

  if (lines().length === 0) {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: { [propKey]: "true" },
          },
        ],
        sections: sectionsParam,
      }),
    });
    const data = await res.json();
    if (data.status || data.errors) return null;
    dispatchSectionRefresh(data);
    const next = await fetchCartJson();
    if (!next?.items) return null;
    cart = next;
  }

  const current = lines();
  const keeper =
    current.find((line) => isFreeGiftLine(line, propKey)) || current[0];
  if (!keeper) return cart;

  for (const line of current) {
    if (line.key === keeper.key) continue;
    const next = await changeGiftLine(line.key, 0, sectionsParam);
    if (!next) return null;
    cart = next;
  }

  const kept = lines().find((line) => line.key === keeper.key) || lines()[0];
  if (!kept) return cart;
  const needsProperty = !isFreeGiftLine(kept, propKey);
  if (needsProperty || Number(kept.quantity) !== 1) {
    const next = await changeGiftLine(
      kept.key,
      1,
      sectionsParam,
      needsProperty ? { [propKey]: "true" } : undefined,
    );
    if (!next) return null;
    cart = next;
  }
  return cart;
}

function readConfig() {
  const el = document.getElementById(CONFIG_ID);
  if (!el?.textContent) return null;
  try {
    return /** @type {ScGiftConfig} */ (JSON.parse(el.textContent));
  } catch {
    return null;
  }
}

function sectionIdsForCart() {
  const ids = [];
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId)
      ids.push(el.dataset.sectionId);
  }
  return ids;
}

function dispatchSectionRefresh(/** @type {Record<string, any>} */ data) {
  const sections = data?.sections;
  if (!sections) return;
  document.dispatchEvent(
    new CartUpdateEvent(data, SOURCE_ID, {
      source: SOURCE_ID,
      sections,
    }),
  );
}

/**
 * Syncs cart goal free gifts (add/remove) when thresholds change.
 * @extends {HTMLElement}
 */
class ScCartGoalsGift extends HTMLElement {
  /** @type {boolean} */
  #inFlight = false;

  /** Cart changed while a sync was running; run once more with a fresh cart. */
  #queued = false;

  /** @type {ReturnType<typeof debounce<() => Promise<void>>>} */
  #debouncedSync;

  constructor() {
    super();
    this.#debouncedSync = debounce(() => this.#syncCartGoalGifts(), 120);
  }

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    queueMicrotask(() => this.#debouncedSync());
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#debouncedSync.cancel();
  }

  /** @param {Event} e */
  #onCartUpdate = (e) => {
    const ev = /** @type {CustomEvent} */ (e);
    if (ev.detail?.data?.source === SOURCE_ID) return;
    if (this.#inFlight) {
      this.#queued = true;
      return;
    }
    this.#debouncedSync();
  };

  async #syncCartGoalGifts() {
    if (this.#inFlight) {
      this.#queued = true;
      return;
    }
    const cfg = readConfig();
    if (!cfg?.gifts?.length) return;

    this.#inFlight = true;
    try {
      const propKey = cfg.propertyKey || FREE_PROP;

      /** @type {Record<string, any> | null} */
      let cart = await fetchCartJson();
      if (!cart?.items) return;

      const sections = sectionIdsForCart();
      const sectionsParam = sections.join(",");

      const eligible = eligibleSubtotalCents(
        cart,
        propKey,
        cfg.excludedProductIds,
      );
      /** @type {Map<number, 0 | 1>} */
      const wanted = new Map();
      for (const rule of cfg.gifts) {
        const variantId = Number(rule.variantId);
        const qualify = eligible >= rule.thresholdCents ? 1 : 0;
        const prev = wanted.get(variantId);
        // Same gift on two thresholds stays as long as any threshold is met.
        wanted.set(variantId, prev === 1 || qualify === 1 ? 1 : 0);
      }

      for (const [variantId, quantity] of wanted) {
        const next = await enforceGiftQuantity(
          cart,
          variantId,
          propKey,
          quantity,
          sectionsParam,
        );
        if (!next?.items) break;
        cart = next;
      }
    } catch (e) {
      console.error(`[${ELEMENT_TAG}]`, e);
    } finally {
      this.#inFlight = false;
      if (this.#queued) {
        this.#queued = false;
        this.#debouncedSync();
      }
    }
  }
}

if (!customElements.get(ELEMENT_TAG)) {
  customElements.define(ELEMENT_TAG, ScCartGoalsGift);
}
